import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Card, Grid, MenuItem, Stack } from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { IStatus } from 'src/@types/Choqorak/Settings/Status';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import useThrottle from 'src/hooks/useThrottle';
// ----------------------------------------------------------------------
const statusType = [
  { value: 1, text: 'Shop' },
  { value: 2, text: 'Floor' },
  { value: 3, text: 'Block' },
  { value: 4, text: 'ElectricBox' },
  { value: 5, text: 'ElectricMeter' },
];
export default observer(function StatusNewEditForm() {
  const { StatusStore } = useStore();
  const { translate } = useLocales();
  const { createStatus, updateStatus, editMode, selectedStatus, clearSelectedStatus } = StatusStore;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewStatusSchema = Yup.object().shape({
    englishName: Yup.string().required(`${translate('Validation.EnglishName')}`),
    dariName: Yup.string().required(`${translate('Validation.DariName')}`),
    pashtoName: Yup.string().required(`${translate('Validation.PashtoName')}`),
    code: Yup.string().required(`${translate('Validation.Code')}`),
  });

  const defaultValues = useMemo<IStatus>(
    () => ({
      id: selectedStatus?.id,
      englishName: selectedStatus?.englishName || '',
      dariName: selectedStatus?.dariName || '',
      pashtoName: selectedStatus?.pashtoName || '',
      code: selectedStatus?.code || '',
    }),
    [selectedStatus]
  );

  const methods = useForm<IStatus>({
    resolver: yupResolver(NewStatusSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = useThrottle((data: IStatus) => {
    if (data.id! === undefined) {
      ///create
      createStatus(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
          navigate(PATH_DASHBOARD.Status.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    } else {
      ///update
      updateStatus(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
          navigate(PATH_DASHBOARD.Status.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    }
  });

  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                name="englishName"
                label={translate('GeneralFields.EnglishName')}
                showAsterisk={true}
                autoFocus
              />
              <RHFTextField
                name="pashtoName"
                label={translate('GeneralFields.PashtoName')}
                showAsterisk={true}
              />
              <RHFTextField
                name="dariName"
                label={translate('GeneralFields.DariName')}
                showAsterisk={true}
              />
              <RHFSelect
                name="statusType"
                label={translate('Status.StatusType')}
                size="medium"
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: 450 } }}
              >
                {statusType.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFTextField
                name="code"
                label={translate('GeneralFields.Code')}
                showAsterisk={true}
              />
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedStatus();
                  navigate(PATH_DASHBOARD.Status.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
